import { action, computed, makeObservable, observable } from 'mobx';
import { Path } from 'react-hook-form';
import { ErrorContextToFieldConverter } from '@alfa-retail-react/components';
import Big from 'big.js';

import { LoadStateStore } from 'app/loading/LoadStateStore';
import { ProductService } from 'app/service/ProductService';
import { CardProduct } from 'app/model/product/CardProduct';
import { IntroductionFormStore } from 'app/logic/pages/introduction/IntroductionFormStore';
import { IntroductionFormValues } from 'app/logic/pages/introduction/IntroductionPage';
import { MapBuilder } from 'app/utils/MapBuilder';
import { CalculatorProps } from 'app/service/types/creditCalculator';

export class IntroductionPageStore {
    private readonly productService = new ProductService();

    readonly loadStateStore = new LoadStateStore();
    readonly loadStateCalculator = new LoadStateStore(true);

    readonly actionStateStore = new LoadStateStore(true);

    formStore: IntroductionFormStore;

    @observable
    private _cardProduct: CardProduct;

    @observable
    private _creditCalculator: CalculatorProps;

    @observable
    private _creditCalculatorSum: Big;

    private _creditCalculatorCache: Map<number, CalculatorProps> = new Map();

    readonly contextConverter =
        new ErrorContextToFieldConverter<IntroductionFormValues>(
            MapBuilder.create<string, Array<Path<IntroductionFormValues>>>()
                .value('client.mobilePhone', ['phone'])
                .defaultValue([])
                .build(),
        );

    constructor() {
        makeObservable(this);
    }

    @action
    async initialize(productId: string, channel: string) {
        return this.loadStateStore.loadData(async () => {
            const cardProduct = await this.productService.getCardProduct(
                productId,
                channel,
            );
            this.setCardProduct(cardProduct);

            this.formStore = IntroductionFormStore.default(cardProduct);
        });
    }

    @action
    async initializeCalculator() {
        if (this._cardProduct.code !== 'RKK') {
            return;
        }

        return this.loadStateCalculator.loadData(async () => {
            const creditCalculator =
                await this.productService.getCreditCalculator(
                    this._cardProduct.code,
                );

            this._creditCalculatorCache.set(
                creditCalculator.sum.amount as unknown as number,
                creditCalculator,
            );

            this.setCreditCalculatorSum(creditCalculator.sum.amount);
            this.setCreditCalculator(creditCalculator);
        });
    }

    @computed
    get cardProduct(): CardProduct {
        return this._cardProduct;
    }

    @action
    setCardProduct(cardProduct: CardProduct) {
        this._cardProduct = cardProduct;
    }

    @computed
    get creditCalculator(): CalculatorProps {
        return this._creditCalculator;
    }

    @action
    setCreditCalculator = (creditCalculator: CalculatorProps) => {
        this._creditCalculator = creditCalculator;
    };

    @computed
    get creditCalculatorSum(): Big {
        return this._creditCalculatorSum;
    }

    @action
    setCreditCalculatorSum = async (sum: Big | number) => {
        this._creditCalculatorSum = new Big(sum || 0);
        const sumNumber = this._creditCalculatorSum.toNumber();

        if (
            sumNumber > this._creditCalculator?.sum.maxValue ||
            sumNumber < this._creditCalculator?.sum.minValue
        ) {
            return;
        }

        if (this._creditCalculatorCache.has(sumNumber)) {
            this.setCreditCalculator(
                this._creditCalculatorCache.get(sumNumber),
            );
        } else {
            this.loadStateCalculator.loadData(async () => {
                const data = await this.productService.getCreditCalculator(
                    this.cardProduct.code,
                    sumNumber,
                );

                this._creditCalculatorCache.set(sumNumber, data);
                this.setCreditCalculator(data);
            });
        }
    };
}
